/**
 * Painel de filtros do Relatório: folha que sobe da base no celular, painel
 * à direita em tablet/desktop. Os filtros aplicam NA HORA (a lista atrás
 * muda enquanto se escolhe) — o botão do rodapé só fecha, dizendo quantos
 * ambientes sobraram.
 *
 * Animação por keyframes CSS, sem framer-motion: o DadosContext re-renderiza
 * pesado e congelava painéis animados em JS (ver SlideOverPanel).
 */
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FiBookmark, FiChevronDown, FiChevronUp, FiTrash2, FiX } from "react-icons/fi";

import { plural } from "../../utils/dinheiro";
import { FASES, ORDENS, SITUACOES, etapasDaFase } from "./dominio";
import {
  LISTAS,
  PERIODOS,
  alternarFase,
  alternarNaLista,
  atalhosDePeriodo,
  estadoDaFase,
  limparFiltros,
  opcoesDaLista,
} from "./filtros";
import { TAMANHO_NOME_VISAO } from "./useEstadoRelatorio";
import {
  BotaoIcone,
  CabecaFolha,
  CampoPequeno,
  Chip,
  Chips,
  CorpoFolha,
  Folha,
  Fundo,
  GrupoFase,
  LinhaDatas,
  LinhaInterruptor,
  LinhaSalvar,
  Recolhivel,
  RodapeFolha,
  Secao,
  VisaoSalva,
} from "./styles";

const BUSCA_A_PARTIR_DE = 12;

function SecaoEtapas({ filtros, setFiltros, contagemPorEtapa }) {
  const [abertas, setAbertas] = useState(() => new Set(FASES.filter((f) => estadoDaFase(filtros, f.id) === "algumas").map((f) => f.id)));
  const alternarAberta = (id) =>
    setAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  return (
    <Secao>
      <h3>Etapa</h3>
      {FASES.map((fase) => {
        const estado = estadoDaFase(filtros, fase.id);
        const etapas = etapasDaFase(fase.id);
        const total = etapas.reduce((soma, etapa) => soma + (contagemPorEtapa[etapa.id] || 0), 0);
        const aberta = abertas.has(fase.id);
        return (
          <GrupoFase key={fase.id}>
            <div className="cabeca">
              <Chip
                type="button"
                $ligado={estado !== "nenhuma"}
                $parcial={estado === "algumas"}
                aria-pressed={estado === "todas"}
                onClick={() => setFiltros((f) => alternarFase(f, fase.id))}
              >
                <span className="nome">{fase.label}</span>
                <small>{total}</small>
              </Chip>
              {etapas.length > 1 && (
                <button
                  type="button"
                  className="abrir"
                  aria-expanded={aberta}
                  aria-label={`${aberta ? "Recolher" : "Abrir"} etapas de ${fase.label}`}
                  onClick={() => alternarAberta(fase.id)}
                >
                  etapas {aberta ? <FiChevronUp size={13} aria-hidden="true" /> : <FiChevronDown size={13} aria-hidden="true" />}
                </button>
              )}
            </div>
            {aberta && etapas.length > 1 && (
              <Chips className="etapas">
                {etapas.map((etapa) => {
                  const ligado = filtros.etapas.includes(etapa.id);
                  return (
                    <Chip
                      key={etapa.id}
                      type="button"
                      $ligado={ligado}
                      aria-pressed={ligado}
                      onClick={() => setFiltros((f) => alternarNaLista(f, "etapas", etapa.id))}
                    >
                      <span className="nome">{etapa.label}</span>
                      <small>{contagemPorEtapa[etapa.id] || 0}</small>
                    </Chip>
                  );
                })}
              </Chips>
            )}
          </GrupoFase>
        );
      })}
    </Secao>
  );
}

function ListaRecolhivel({ campo, label, ambientes, filtros, setFiltros }) {
  const escolhidos = filtros[campo];
  const [aberta, setAberta] = useState(escolhidos.length > 0);
  const [busca, setBusca] = useState("");
  const opcoes = useMemo(
    () => (aberta ? opcoesDaLista(ambientes, filtros, campo) : []),
    [aberta, ambientes, filtros, campo]
  );
  const visiveis = busca.trim()
    ? opcoes.filter((o) => o.valor.toLocaleLowerCase("pt-BR").includes(busca.trim().toLocaleLowerCase("pt-BR")))
    : opcoes;
  const resumo = escolhidos.length === 0 ? "Todos" : escolhidos.length === 1 ? escolhidos[0] : `${escolhidos.length} escolhidos`;

  return (
    <Recolhivel>
      <button type="button" className="cabeca" aria-expanded={aberta} onClick={() => setAberta((v) => !v)}>
        <span>{label}</span>
        <span className="valor" data-ativo={escolhidos.length ? "sim" : "nao"}>
          {resumo}
        </span>
        {aberta ? <FiChevronUp size={14} aria-hidden="true" /> : <FiChevronDown size={14} aria-hidden="true" />}
      </button>
      {aberta && (
        <div className="corpo">
          {opcoes.length > BUSCA_A_PARTIR_DE && (
            <CampoPequeno
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={`Buscar ${label.toLowerCase()}`}
              aria-label={`Buscar ${label.toLowerCase()}`}
            />
          )}
          <Chips>
            {visiveis.map((opcao) => {
              const ligado = escolhidos.includes(opcao.valor);
              return (
                <Chip
                  key={opcao.valor}
                  type="button"
                  $ligado={ligado}
                  aria-pressed={ligado}
                  onClick={() => setFiltros((f) => alternarNaLista(f, campo, opcao.valor))}
                >
                  <span className="nome">{opcao.valor}</span>
                  <small>{opcao.total}</small>
                </Chip>
              );
            })}
          </Chips>
        </div>
      )}
    </Recolhivel>
  );
}

function PeriodoRecolhivel({ campo, label, filtros, setFiltros }) {
  const periodo = filtros[campo];
  const ativo = Boolean(periodo.de || periodo.ate);
  const [aberto, setAberto] = useState(ativo);
  const atalhos = useMemo(() => atalhosDePeriodo(campo), [campo]);
  const definir = (parte) => setFiltros((f) => ({ ...f, [campo]: { ...f[campo], ...parte } }));

  return (
    <Recolhivel>
      <button type="button" className="cabeca" aria-expanded={aberto} onClick={() => setAberto((v) => !v)}>
        <span>{label}</span>
        <span className="valor" data-ativo={ativo ? "sim" : "nao"}>
          {ativo ? "Com período" : "Qualquer data"}
        </span>
        {aberto ? <FiChevronUp size={14} aria-hidden="true" /> : <FiChevronDown size={14} aria-hidden="true" />}
      </button>
      {aberto && (
        <div className="corpo">
          <Chips>
            {atalhos.map((atalho) => {
              const ligado = periodo.de === atalho.de && periodo.ate === atalho.ate;
              return (
                <Chip
                  key={atalho.id}
                  type="button"
                  $ligado={ligado}
                  aria-pressed={ligado}
                  onClick={() => definir(ligado ? { de: "", ate: "" } : { de: atalho.de, ate: atalho.ate })}
                >
                  {atalho.label}
                </Chip>
              );
            })}
          </Chips>
          <LinhaDatas>
            <CampoPequeno
              type="date"
              value={periodo.de}
              max={periodo.ate || undefined}
              onChange={(e) => definir({ de: e.target.value })}
              aria-label={`${label}: de`}
            />
            <span>até</span>
            <CampoPequeno
              type="date"
              value={periodo.ate}
              min={periodo.de || undefined}
              onChange={(e) => definir({ ate: e.target.value })}
              aria-label={`${label}: até`}
            />
          </LinhaDatas>
        </div>
      )}
    </Recolhivel>
  );
}

function SecaoVisoes({ visoes, visaoAtivaId, onAplicar, onSalvar, onExcluir }) {
  const [nome, setNome] = useState("");
  const [confirmando, setConfirmando] = useState(null);
  return (
    <Secao>
      <h3>Visões salvas</h3>
      {visoes.length > 0 && (
        <Chips>
          {visoes.map((visao) => (
            <VisaoSalva key={visao.id}>
              <Chip
                type="button"
                $ligado={visao.id === visaoAtivaId}
                aria-pressed={visao.id === visaoAtivaId}
                onClick={() => onAplicar(visao.id)}
              >
                <FiBookmark size={12} aria-hidden="true" />
                <span className="nome">{visao.nome}</span>
              </Chip>
              <button
                type="button"
                className="excluir"
                aria-label={confirmando === visao.id ? `Confirmar exclusão de ${visao.nome}` : `Excluir visão ${visao.nome}`}
                title={confirmando === visao.id ? "Toque de novo para excluir" : "Excluir visão"}
                onClick={() => {
                  if (confirmando === visao.id) {
                    onExcluir(visao.id);
                    setConfirmando(null);
                  } else setConfirmando(visao.id);
                }}
                onBlur={() => setConfirmando(null)}
              >
                {confirmando === visao.id ? "Excluir?" : <FiTrash2 size={12} aria-hidden="true" />}
              </button>
            </VisaoSalva>
          ))}
        </Chips>
      )}
      <LinhaSalvar
        onSubmit={(e) => {
          e.preventDefault();
          if (onSalvar(nome)) setNome("");
        }}
      >
        <CampoPequeno
          value={nome}
          maxLength={TAMANHO_NOME_VISAO}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Nome para a visão atual"
          aria-label="Nome para salvar a visão atual"
        />
        <button type="submit" className="btn btn--secondary" disabled={!nome.trim()}>
          Salvar
        </button>
      </LinhaSalvar>
    </Secao>
  );
}

/**
 * Props: aberto, onFechar, ambientes (universo em aberto), filtros,
 * setFiltros, ordem, setOrdem, totalFiltrado, contagemPorEtapa,
 * contagemPorSituacao, visoes, visaoAtivaId, onAplicarVisao, onSalvarVisao,
 * onExcluirVisao.
 */
export default function PainelFiltros({
  aberto,
  onFechar,
  ambientes,
  filtros,
  setFiltros,
  ordem,
  setOrdem,
  totalFiltrado,
  contagemPorEtapa,
  contagemPorSituacao,
  visoes,
  visaoAtivaId,
  onAplicarVisao,
  onSalvarVisao,
  onExcluirVisao,
}) {
  useEffect(() => {
    if (!aberto) return undefined;
    const aoTeclar = (e) => {
      if (e.key === "Escape") onFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = overflowAnterior;
    };
  }, [aberto, onFechar]);

  if (!aberto || typeof document === "undefined") return null;

  return createPortal(
    <Fundo onClick={onFechar} role="presentation">
      <Folha role="dialog" aria-modal="true" aria-label="Filtros do relatório" onClick={(e) => e.stopPropagation()}>
        <CabecaFolha>
          <h2>Filtros</h2>
          <BotaoIcone type="button" onClick={onFechar} aria-label="Fechar filtros">
            <FiX size={16} aria-hidden="true" />
          </BotaoIcone>
        </CabecaFolha>

        <CorpoFolha>
          <SecaoVisoes
            visoes={visoes}
            visaoAtivaId={visaoAtivaId}
            onAplicar={onAplicarVisao}
            onSalvar={onSalvarVisao}
            onExcluir={onExcluirVisao}
          />

          <Secao>
            <h3>Ordenar por</h3>
            <Chips>
              {ORDENS.map((opcao) => (
                <Chip
                  key={opcao.id}
                  type="button"
                  $ligado={ordem === opcao.id}
                  aria-pressed={ordem === opcao.id}
                  onClick={() => setOrdem(opcao.id)}
                >
                  {opcao.label}
                </Chip>
              ))}
            </Chips>
          </Secao>

          <Secao>
            <h3>Situação</h3>
            <Chips>
              {SITUACOES.map((situacao) => {
                const ligado = filtros.situacoes.includes(situacao.id);
                return (
                  <Chip
                    key={situacao.id}
                    type="button"
                    $ligado={ligado}
                    aria-pressed={ligado}
                    onClick={() => setFiltros((f) => alternarNaLista(f, "situacoes", situacao.id))}
                  >
                    <span className="nome">{situacao.label}</span>
                    <small>{contagemPorSituacao[situacao.id] || 0}</small>
                  </Chip>
                );
              })}
            </Chips>
          </Secao>

          <SecaoEtapas filtros={filtros} setFiltros={setFiltros} contagemPorEtapa={contagemPorEtapa} />

          <Secao>
            <h3>Pessoas, lote e tipo</h3>
            {LISTAS.map(({ campo, label }) => (
              <ListaRecolhivel
                key={campo}
                campo={campo}
                label={label}
                ambientes={ambientes}
                filtros={filtros}
                setFiltros={setFiltros}
              />
            ))}
          </Secao>

          <Secao>
            <h3>Períodos</h3>
            {PERIODOS.map(({ campo, label }) => (
              <PeriodoRecolhivel key={campo} campo={campo} label={label} filtros={filtros} setFiltros={setFiltros} />
            ))}
          </Secao>

          <Secao>
            <LinhaInterruptor>
              <span>
                Incluir obra futura
                <small>Ambientes marcados como OBRA FUTURA = SIM na planilha.</small>
              </span>
              <input
                type="checkbox"
                checked={filtros.obraFutura}
                onChange={(e) => setFiltros((f) => ({ ...f, obraFutura: e.target.checked }))}
              />
            </LinhaInterruptor>
          </Secao>
        </CorpoFolha>

        <RodapeFolha>
          <button type="button" className="btn btn--ghost" onClick={() => setFiltros((f) => limparFiltros(f))}>
            Limpar
          </button>
          <button type="button" className="btn btn--primary principal" onClick={onFechar}>
            {`Ver ${plural(totalFiltrado, "ambiente", "ambientes")}`}
          </button>
        </RodapeFolha>
      </Folha>
    </Fundo>,
    document.body
  );
}
